import { Menu, Transition } from "@headlessui/react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { DateRangePicker as RangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useRangePicker } from "../store/use-range-picker-model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

interface IDateRangePicker {
  children: React.ReactNode;
  onChange: (e: Record<string, any>) => void;
}

const DateRangePicker = ({ children, onChange }: IDateRangePicker) => {
  const { initialValues, onCloseRangePicker } = useRangePicker();
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  }>({ top: 0, left: 0 });

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
    new: true,
  });

  /**
   * Anchor the panel to whichever trigger edge faces the middle of the viewport, so
   * it always opens back across the page rather than off it.
   *
   * Pinning one edge for every trigger cannot work: the dashboard's calendar is
   * a left-aligned pill in a filter row, while the chase list's is in the top-right
   * toolbar. Left-anchoring the chase list pushed the ~600px panel off the right,
   * and clamping it back inside the viewport dragged it clear across to the sidebar.
   * Choosing the edge by trigger position also removes the width measurement, which
   * read 0 on the first open before the calendar had laid out.
   */
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const height = panelRef.current?.offsetHeight ?? 0;
    const margin = 8;

    // Flip above the trigger when there isn't room below it.
    let top = rect.bottom + 10;
    if (height && top + height > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - height - 10);
    }

    const anchorRight = rect.left + rect.width / 2 > window.innerWidth / 2;
    const next = anchorRight
      ? { top, right: Math.max(margin, window.innerWidth - rect.right) }
      : { top, left: Math.max(margin, rect.left) };

    // Bail out when nothing moved — the panel is observed for resize, and an
    // unconditional setState would feed that observer back into itself.
    setPanelPos((prev) =>
      prev.top === next.top &&
      prev.left === next.left &&
      prev.right === next.right
        ? prev
        : next
    );
  }, []);

  useLayoutEffect(() => {
    if (!initialValues.isOpen) return;
    reposition();
  }, [initialValues.isOpen, reposition]);

  /**
   * The first reposition can run before the calendar has its final height, so the
   * flip-above check would see 0. Re-run once the panel has settled.
   */
  useEffect(() => {
    const panel = panelRef.current;
    if (!initialValues.isOpen || !panel || typeof ResizeObserver === "undefined")
      return;
    const observer = new ResizeObserver(() => reposition());
    observer.observe(panel);
    return () => observer.disconnect();
  }, [initialValues.isOpen, reposition]);

  useEffect(() => {
    if (!initialValues.isOpen) return;
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [initialValues.isOpen, reposition]);

  const handleSelect = (e) => {
    const dataSelection = {
      startDate: moment(e.selection?.startDate).format("YYYY-MM-DD"),
      endDate:
        e.selection?.startDate === e.selection?.endDate
          ? null
          : moment(e.selection?.endDate).format("YYYY-MM-DD"),
      key: "selection",
    };

    onChange(dataSelection);
    setDateRange({ ...e.selection, new: false });
  };

  const resetFilter = () => {
    const dataSelection = {
      startDate: null,
      endDate: null,
      key: "selection",
    };

    onChange(dataSelection);
    setDateRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
      new: true,
    });
  };

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 640;

  const panel =
    typeof document !== "undefined"
      ? createPortal(
          <Transition
            as="div"
            className="fixed inset-0 z-[100000] pointer-events-none"
            enter="transition ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={initialValues.isOpen}
          >
            <div
              className="absolute inset-0 sm:hidden bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm pointer-events-auto"
              onClick={onCloseRangePicker}
            />
            <div
              className="hidden sm:block absolute inset-0 pointer-events-auto"
              onClick={onCloseRangePicker}
            />

            <div
              ref={panelRef}
              style={
                isMobile
                  ? undefined
                  : {
                      top: panelPos.top,
                      // Only one of these is ever set; the unset edge stays `auto`
                      // via sm:left-auto so the anchored edge governs.
                      left: panelPos.left,
                      right: panelPos.right,
                      // Never let the panel run off the edge it grows toward.
                      maxWidth: `calc(100vw - ${
                        (panelPos.left ?? panelPos.right ?? 0) + 8
                      }px)`,
                    }
              }
              className="
                absolute
                left-1/2 sm:left-auto top-1/2 sm:top-auto
                -translate-x-1/2 -translate-y-1/2 sm:translate-x-0 sm:translate-y-0
                w-[min(95vw,360px)] sm:w-auto
                p-3 sm:p-4
                rounded-2xl border border-edge shadow-overlay
                flex items-center justify-center
                bg-surface-elevated
                animate-fade-in
                overflow-hidden
                pointer-events-auto
              "
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-h-[80vh] overflow-auto custom-scrollbar">
                <RangePicker
                  ranges={[dateRange]}
                  onChange={handleSelect}
                  rangeColors={["#6366F1"]}
                  showMonthAndYearPickers={true}
                />
              </div>
            </div>
          </Transition>,
          document.body
        )
      : null;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div className="flex items-center gap-2" ref={triggerRef}>
        <Menu.Button data-bs-toggle="dropdown" className="cursor-pointer">
          {children}
        </Menu.Button>
        {!dateRange.new && (
          <div
            onClick={resetFilter}
            className="cursor-pointer px-4 rounded-full appearance-none text-[16px] flex items-center justify-center h-[50px] bg-surface-elevated text-light-gray border border-edge outline-none hover:border-indigo-400/40 transition-all duration-300"
          >
            <FontAwesomeIcon icon={faCalendarXmark} />
          </div>
        )}
      </div>

      {panel}
    </Menu>
  );
};

export default DateRangePicker;
